import type { ErrorCode, ErrorEnvelope } from "@docmind/types";

const API_URL = import.meta.env["VITE_API_URL"] ?? "http://localhost:8080";

export class ApiError extends Error {
  constructor(
    readonly code: ErrorCode | "NETWORK",
    message: string,
    readonly status: number,
    readonly details?: Record<string, unknown>,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  signal?: AbortSignal;
}

/**
 * The single place the frontend talks to the gateway.
 *
 * credentials: "include" is what carries the httpOnly session cookie to
 * api.docmind.dpdns.org. It only works because the gateway sets an explicit
 * CORS origin -- browsers refuse credentialed requests against a wildcard (W-5).
 *
 * Day 7 note: when refresh rotation lands, the 401 branch below gains a
 * retry-after-refresh step, and concurrent 401s must share ONE in-flight
 * refresh promise or five parallel requests fire five rotations that
 * invalidate each other (W-20). There is no refresh endpoint yet, so a 401
 * today simply means "sign in again" -- building the single-flight machinery
 * now would be unused code guarding a case that cannot occur.
 */
export async function apiFetch<T>(
  path: string,
  { method = "GET", body, signal }: RequestOptions = {},
): Promise<T> {
  let response: Response;

  try {
    response = await fetch(`${API_URL}${path}`, {
      method,
      credentials: "include",
      headers: body ? { "Content-Type": "application/json" } : {},
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
      ...(signal ? { signal } : {}),
    });
  } catch (err) {
    // fetch only rejects for network-level failures; HTTP errors resolve.
    if (signal?.aborted) throw err;
    throw new ApiError(
      "NETWORK",
      "Could not reach the server. Check your connection.",
      0,
    );
  }

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  const parsed: unknown = text ? JSON.parse(text) : undefined;

  if (!response.ok) {
    const envelope = parsed as ErrorEnvelope | undefined;
    throw new ApiError(
      envelope?.error?.code ?? "INTERNAL",
      envelope?.error?.message ?? `Request failed (${response.status})`,
      response.status,
      envelope?.error?.details,
    );
  }

  return parsed as T;
}
